import { getCurrentInstance, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useDialog } from './useDialog'
import { useSlideshow } from '../slideshow/useSlideshow'
import { useMenuMessage } from '../../messages/useMenuMessage'
import { useDialogMessage } from '../../messages/useDialogMessage'
import type { LocationDialogProps } from './useLocationDialog'
import { useInactiveTimeout } from '../timeout/useInactiveTimeout'
import { config } from '@/core/config'
import { logTime } from '@/core/utils'

export function useLocationDialogWithTimeout(props: LocationDialogProps) {
  const { id, data, autoOpen } = props
  const dialog = useDialog(id)
  const slideshow = useSlideshow(data ? data.length : 0)
  const location = ref<HTMLDivElement>()

  const menuMessage = useMenuMessage(id)
  const dialogMessage = useDialogMessage(id)
  const dialogTimeout = useInactiveTimeout(id + '.dialog', config.locationSettings.dialogTimeoutSec * 1000)
  const slideshowTimeout = useInactiveTimeout(
    id + '.slideshow',
    config.locationSettings.slideshowTimeoutSec * 1000,
    false
  )

  function resetAllTimeout() {
    dialogTimeout.reset()
    slideshowTimeout.reset()
  }

  onMounted(() => {
    location.value = <HTMLDivElement>getCurrentInstance()?.refs.location
    location.value?.querySelector('.next')?.addEventListener('pointerdown', resetAllTimeout)
    location.value?.querySelector('.prev')?.addEventListener('pointerdown', resetAllTimeout)
  })

  // onBeforeUnmount(() => {
  //   location.value?.querySelector('.next')?.removeEventListener('pointerdown', resetAllTimeout)
  //   location.value?.querySelector('.prev')?.removeEventListener('pointerdown', resetAllTimeout)
  //   location.value
  //     ?.querySelector<HTMLVideoElement>('video.preview')
  //     ?.removeEventListener('ended', slideshowTimeout.reset)
  // })

  dialog.onDialogOpened(() => {
    //------------------
    dialogTimeout.reset()
    slideshowTimeout.reset()
    //------------------
    const menuDisableId = id.replace('location', 'menu')
    const menuEnableId = menuDisableId.endsWith('-th')
      ? menuDisableId.replace('-th', '-en')
      : menuDisableId.replace('-en', '-th')
    const dialogCloseId = id.endsWith('-th') ? id.replace('-th', '-en') : id.replace('-en', '-th')
    menuMessage.DisableMenuCommand(menuDisableId)
    menuMessage.EnableMenuCommand(menuEnableId)
    dialogMessage.CloseDialogCommand(dialogCloseId)
    //------------------
    slideshow.currentIndex.value = 0
    previewVideoPauseAll(location.value)
    previewVideoPlayByIndex(location.value, slideshow.currentIndex.value)
  })

  dialog.onDialogClosed(() => {
    //------------------
    dialogTimeout.stop()
    slideshowTimeout.stop()
    //------------------
    const menuEnableId = id.replace('location', 'menu')
    menuMessage.EnableMenuCommand(menuEnableId)
    //------------------
    previewVideoPauseAll(location.value)
  })

  slideshow.onCurrentIndexChanged((index) => {
    previewVideoPauseAll(location.value)
    previewVideoPlayByIndex(location.value, index)
  })

  slideshow.onVideoEnded((index) => {
    // console.log('onVideoEnded', { index })
    slideshowTimeout.stop()
    setTimeout(() => {
      slideshow.next()
      slideshowTimeout.reset()
    }, 800)
  })

  dialogTimeout.onTimeout(() => {
    dialog.close()
  })

  slideshowTimeout.onTimeout(() => {
    const el = location.value?.querySelector(`.preview[index='${slideshow.currentIndex.value}']`)
    if (el?.tagName !== 'VIDEO') {
      slideshow.next()
    }
    slideshowTimeout.reset()
  })

  function previewVideoPauseAll(parentElement: HTMLDivElement | undefined) {
    if (parentElement) {
      let videos = parentElement.querySelectorAll<HTMLVideoElement>('video.preview')
      videos.forEach((el) => {
        if (!el.paused) {
          el.pause()
        }
      })
    }
  }

  function previewVideoPlayByIndex(parentElement: HTMLDivElement | undefined, index: number) {
    if (parentElement) {
      let video = parentElement.querySelector<HTMLVideoElement>(`video.preview[index='${index}']`)
      if (video) {
        video.currentTime = 0
        video.play()
      }
    }
  }

  onMounted(() => {
    if (autoOpen) {
      setTimeout(() => {
        dialog.open()
      }, 800)
    }
  })

  return {
    id,
    data,
    ...dialog,
    ...slideshow,
  }
}
